/**
 * Offer type sub-capabilities under the canonical OFFERS module.
 * Platform defaults enable all types when OFFERS is on; Admin can disable types individually.
 */

export const SERVICE_OFFER_TYPES = [
  'digital',
  'one_to_one',
  'priority_dm',
  'webinar',
  'cohort',
  'package',
] as const;

export type ServiceOfferType = (typeof SERVICE_OFFER_TYPES)[number];

export const OFFER_TYPE_LABELS: Record<ServiceOfferType, string> = {
  digital: 'Digital downloads',
  one_to_one: '1:1 sessions',
  priority_dm: 'Priority DM',
  webinar: 'Webinars',
  cohort: 'Cohorts',
  package: 'Packages',
};

export function isServiceOfferType(value: string): value is ServiceOfferType {
  return (SERVICE_OFFER_TYPES as readonly string[]).includes(value);
}

export function getDefaultOfferTypeCapabilities(): Record<ServiceOfferType, boolean> {
  return Object.fromEntries(SERVICE_OFFER_TYPES.map((type) => [type, true])) as Record<
    ServiceOfferType,
    boolean
  >;
}

export function normalizeOfferTypeCapabilities(
  input?: Partial<Record<ServiceOfferType, boolean>> | null,
): Record<ServiceOfferType, boolean> {
  const defaults = getDefaultOfferTypeCapabilities();
  if (!input) return defaults;
  for (const type of SERVICE_OFFER_TYPES) {
    if (typeof input[type] === 'boolean') {
      defaults[type] = input[type]!;
    }
  }
  return defaults;
}

export function getOfferTypeEnvVarName(type: ServiceOfferType): string {
  return `LOAMIFY_FEATURE_OFFERS_${type.toUpperCase()}`;
}

export function getOfferTypeCapabilitiesFromEnv(
  env: NodeJS.ProcessEnv = process.env,
): Partial<Record<ServiceOfferType, boolean>> {
  const result: Partial<Record<ServiceOfferType, boolean>> = {};
  for (const type of SERVICE_OFFER_TYPES) {
    const raw = env[getOfferTypeEnvVarName(type)];
    if (raw === undefined || raw === '') continue;
    result[type] = raw === '1' || raw.toLowerCase() === 'true';
  }
  return result;
}
